import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {useDebounceValue} from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import {PageHeader} from '../components/page-header'
import styles from '../index.module.css'
import {FriendlyDateTime} from "../../shared/Date/friendly-date-time";
import {SearchInput} from '../components/search-input'
import {Modal} from '../../shared/NarusMaterial/Modal'


export interface Log extends Table.Item {
    id: number
    user_id: string
    created_at: string
    updated_at: string
    email: string
    email_subject: string | null
    email_body: string | null
    email_recipients: string[] | null
    employer: string | null
    first_name: string | null
    last_name: string | null
    message: string
    patient_id: string | null
    phone: string | null
    topics: string[]
    app_variant: string
}

interface ApiResponse {
    ok: boolean
    data: {
        logs: Log[]
        meta: {
            total: number
        }
    }
}

export function PortalIndex(): JSX.Element {
    const [query, setQuery] = useState('')
    const [page, setPage] = useState(0) // 0-based
    const [pageSize, setPageSize] = useState(10)
    const [selectedTicket, setSelectedTicket] = useState<Log | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [relationshipFilter, setRelationshipFilter] = useState<string>('')
    const [memberId, setMemberId] = useState<string>('')
    const [lucentMemberId, setLucentMemberId] = useState<string>('')
    const [userEmail, setUserEmail] = useState<string>('')

    // Sorting
    const [sort, setSort] = useState<Table.Sort<Log>>({field: 'created_at', direction: 'desc'})

    // Debounced filter values to avoid excessive requests
    const [debouncedQuery] = useDebounceValue(query, 600)
    const [debouncedStatus] = useDebounceValue(statusFilter, 400)
    const [debouncedRelationship] = useDebounceValue(relationshipFilter, 400)
    const [debouncedMemberId] = useDebounceValue(memberId, 600)
    const [debouncedLucentMemberId] = useDebounceValue(lucentMemberId, 600)
    const [debouncedUserEmail] = useDebounceValue(userEmail, 600)

    const {data, status, error} = useQuery<ApiResponse>({
        queryKey: ['support-tickets', {
            page,
            pageSize,
            statusFilter: debouncedStatus,
            relationshipFilter: debouncedRelationship,
            memberId: debouncedMemberId,
            lucentMemberId: debouncedLucentMemberId,
            userEmail: debouncedUserEmail,
            sort
        }],
        queryFn: async () => {
            const sp = new URLSearchParams([
                ['page', (page + 1).toString()],
                ['pageSize', pageSize.toString()],
            ] as any)


            if (debouncedQuery.trim()) sp.set('query', debouncedQuery.trim())
            if (debouncedStatus.trim()) sp.set('status', debouncedStatus.trim())
            if (debouncedRelationship.trim()) sp.set('relationship', debouncedRelationship.trim())
            if (debouncedMemberId.trim()) sp.set('memberId', debouncedMemberId.trim())
            if (debouncedLucentMemberId.trim()) sp.set('lucentMemberId', debouncedLucentMemberId.trim())
            if (debouncedUserEmail.trim()) sp.set('userEmail', debouncedUserEmail.trim())
            if (sort?.field) {
                sp.set('sortBy', sort.field as string)
                sp.set('sortDir', sort.direction)
            }

            const response = await http.getRequest<ApiResponse>(`/admin/support-tickets?${sp.toString()}`)
            return response
        },
        placeholderData: keepPreviousData,
    })

    const columns = useMemo<Array<Table.Column<Log>>>(() => [
        {
            name: 'created_at',
            header: "Created At",
            headerStyle: {
                display: 'flex',
                justifyContent: 'right'
            },
            style: {
                display: 'flex',
                justifyContent: 'right'
            },
            getValue: (row) => <FriendlyDateTime datetime={row.created_at}/>
        },
        {
            name: 'id',
            header: 'Ticket ID',
            getValue: (row) => row.id
        },
        {
            name: 'user_name',
            header: 'User Name',
            getValue: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || '-'
        },
        {
            name: 'email',
            header: 'Email (From)',
            getValue: (row) => row.email
        },
        {
            name: 'phone',
            header: 'Phone',
            getValue: (row) => row.phone || '-'
        },
        {
            name: 'email_recipients',
            header: 'Recipients',
            getValue: (row) => row.email_recipients?.join(", ") || '-'
        },
        {
            name: 'email_subject',
            header: 'Email Subject',
            gridArea: '2fr',
            getValue: (row) => row.email_subject || '-'
        },
        {
            name: 'topics',
            header: 'Topics',
            getValue: (row) => row.topics?.join(", ") || '-'
        },
        {
            name: 'message',
            header: 'Message',
            gridArea: '2fr'
        },
        {
            name: 'actions',
            header: 'Actions',
            getValue: (row) => (
                <button
                    onClick={() => {
                        setSelectedTicket(row)
                        setIsModalOpen(true)
                    }}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    View Details
                </button>
            )
        }
    ], [])

    const pagination = useMemo(() => ({
        page,
        pageSize,
        totalFiltered: data?.data?.meta.total ?? 0,
        totalUnfiltered: data?.data?.meta.total ?? 0,
    }), [page, pageSize, data?.data?.meta.total])

    return (
        <div>
            <PageHeader className={styles.pageHeader}>
                <h1>Support Tickets</h1>
                <SearchInput
                    value={query}
                    placeholder='Search by patient info, message'
                    onChange={event => {
                        setQuery(event.target.value)
                        setPage(0)
                    }}
                />
            </PageHeader>

            <Table.Table
                data={data?.data?.logs ?? []}
                queryStatus={status}
                error={error as Error | undefined}
                noDataPlaceholder='No patients to display'
                columns={columns}
                sort={sort}
                onSortChange={(field, direction) => setSort({field: field as keyof Log, direction})}
                pagination={pagination}
                onPageChange={(value: number) => setPage(value - 1)}
                onPageSizeChange={(value: number) => {
                    setPageSize(value);
                    setPage(0)
                }}
                filters={[
                    {
                        name: 'active', value: statusFilter ? [statusFilter] : [], options: [
                            {value: 'Active'},
                            {value: 'Inactive'},
                        ]
                    },
                    {
                        name: 'relationship', value: relationshipFilter ? [relationshipFilter] : [], options: [
                            {value: 'Enrollee'},
                            {value: 'Dependent'},
                        ]
                    },
                    {
                        name: 'member_id',
                        value: memberId ? [memberId] : [],
                        render: ({onChange, value}) => (
                            <div style={{padding: '0.5rem'}}>
                                <input
                                    autoFocus
                                    type='text'
                                    defaultValue={(value?.[0] as string) ?? ''}
                                    placeholder='Filter by Alt Member ID'
                                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                    style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                />
                            </div>
                        ),
                        isActive: !!memberId
                    },
                    {
                        name: 'lucent_member_id',
                        value: lucentMemberId ? [lucentMemberId] : [],
                        render: ({onChange, value}) => (
                            <div style={{padding: '0.5rem'}}>
                                <input
                                    autoFocus
                                    type='text'
                                    defaultValue={(value?.[0] as string) ?? ''}
                                    placeholder='Filter by Member ID'
                                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                    style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                />
                            </div>
                        ),
                        isActive: !!lucentMemberId
                    },
                    {
                        name: 'matched_users', value: userEmail ? [userEmail] : [], render: ({onChange, value}) => (
                            <div style={{padding: '0.5rem'}}>
                                <input
                                    autoFocus
                                    type='text'
                                    defaultValue={(value?.[0] as string) ?? ''}
                                    placeholder='Filter by user email'
                                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                    style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                />
                            </div>
                        ), isActive: !!userEmail
                    },
                ]}
                onFilterChange={(field, values) => {
                    const first = (values?.[0] as string) ?? ''
                    if (field === 'active') setStatusFilter(first)
                    if (field === 'relationship') setRelationshipFilter(first)
                    if (field === 'member_id') setMemberId(first)
                    if (field === 'lucent_member_id') setLucentMemberId(first)
                    if (field === 'matched_users') setUserEmail(first)
                    setPage(0)
                }}
                onFiltersClear={() => {
                    setStatusFilter('')
                    setRelationshipFilter('')
                    setMemberId('')
                    setLucentMemberId('')
                    setUserEmail('')
                    setPage(0)
                }}
            />

            {isModalOpen && selectedTicket && (
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        setSelectedTicket(null)
                    }}
                    title={`Ticket #${selectedTicket.id} - Details`}
                >
                    <div style={{padding: '1rem'}}>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Created At:</strong> <FriendlyDateTime datetime={selectedTicket.created_at}/>
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>User:</strong> {[selectedTicket.first_name, selectedTicket.last_name].filter(Boolean).join(" ") || '-'}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Email (From):</strong> {selectedTicket.email}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Phone:</strong> {selectedTicket.phone || '-'}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Recipients:</strong> {selectedTicket.email_recipients?.join(", ") || '-'}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Topics:</strong> {selectedTicket.topics?.join(", ") || '-'}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Email Subject:</strong> {selectedTicket.email_subject || '-'}
                        </div>
                        <div style={{marginBottom: '1rem'}}>
                            <strong>Message:</strong>
                            <div style={{marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                                {selectedTicket.message}
                            </div>
                        </div>
                        {selectedTicket.email_body && (
                            <div style={{marginBottom: '1rem'}}>
                                <strong>Email Body:</strong>
                                <div
                                    style={{
                                        marginTop: '0.5rem',
                                        padding: '1rem',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        maxHeight: '400px',
                                        overflow: 'auto',
                                        border: '1px solid #ddd'
                                    }}
                                    dangerouslySetInnerHTML={{__html: selectedTicket.email_body}}
                                />
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    )
}

export default PortalIndex
